import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { ArrowRight, Coffee, Mail } from 'lucide-react';
import { PageSeo } from './PageSeo';
import { BackToToday, PageHeader } from './SiteChrome';
import { ContentLayout } from './PageSidebar';
import { SiteFooter } from './SiteFooter';
import { PAGE_META } from '../lib/seo';
import { getNewsUnreadCount, NEWS_SEEN_KEY } from '../lib/news';

const CONTACT_EMAIL = 'eliass.lalaoui@gmail.com';

export function AboutPage() {
  const [newsUnreadCount, setNewsUnreadCount] = useState(0);

  useEffect(() => {
    try {
      setNewsUnreadCount(getNewsUnreadCount(localStorage.getItem(NEWS_SEEN_KEY)));
    } catch {
      setNewsUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#contact') {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <div className="relative pb-8">
      <PageSeo meta={PAGE_META.about} />
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-24 w-80 h-80 rounded-full bg-[#22d3ee]/10 blur-3xl" />
        <div className="absolute top-1/3 -right-24 w-72 h-72 rounded-full bg-[#facc15]/10 blur-3xl" />
        <div className="absolute -bottom-16 left-1/4 w-64 h-64 rounded-full bg-[#CD7F32]/[0.08] blur-3xl" />
      </div>

      <PageHeader newsUnreadCount={newsUnreadCount} />

      <ContentLayout current="about">
      <motion.div initial={false} animate={{ opacity: 1, y: 0 }}>
        <BackToToday />

        <div className="text-[11px] font-black uppercase tracking-[0.25em] text-foreground/40 mb-3">
          The story
        </div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-none mb-6">About us</h1>

        <div className="h-1.5 w-24 rounded-full bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)] mb-8" />

        <div className="space-y-5 text-[15px] leading-relaxed text-foreground/75">
          <p>
            Centumth started as a game I played with friends. I’d pick a topic, write a Top 100 in
            secret, and they’d try to guess their way down the list. Every answer got ranked by hand
            with arguments included. Chaotic. Competitive. Kind of perfect.
          </p>
          <p>
            I did that for a long time before it clicked that the whole thing wanted to be a daily
            game: one topic, a hidden Top 100, five guesses, chase the answers closest to #100.
          </p>
          <p>
            So I built it, put it online, and people started playing. That still feels a little unreal.
          </p>
          <p>
            A new topic drops every midnight. Each list is researched and ranked by a person, not
            generated on the fly. Sources are the starting point — named on the puzzle when we can
            show them — and the final order can include small editorial calls so the game stays
            balanced. The interesting answers are the ones near #100: true, a little obscure, and
            still fair if you think sideways.
          </p>
          <p>
            If a ranking feels off, say so. Player feedback is how the lists got stricter, and why
            the game keeps changing.
          </p>
        </div>

        <section className="mt-12">
          <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40 mb-4">
            The team
          </div>
          <div className="rounded-3xl border border-border bg-muted/30 divide-y divide-border overflow-hidden">
            <div className="flex flex-col sm:flex-row items-center gap-6 p-5 sm:p-6">
              <div className="relative shrink-0 w-44 sm:w-40">
                <div
                  aria-hidden
                  className="absolute -inset-[3px] rounded-[1.35rem] bg-[linear-gradient(to_bottom,#CD7F32,#facc15,#22d3ee)] opacity-80"
                />
                <img
                  src="/mellow.jpeg"
                  alt="Mellow, a small fluffy kitten"
                  className="relative w-full aspect-[3/4] object-cover object-[50%_20%] rounded-[1.2rem]"
                />
              </div>
              <div className="text-center sm:text-left min-w-0">
                <div className="text-[11px] font-black uppercase tracking-widest text-foreground/40 mb-1">
                  Head of morale
                </div>
                <h2 className="text-2xl font-black tracking-tight mb-2">Mellow</h2>
                <p className="text-sm text-foreground/65 leading-relaxed">
                  Quality control, snack diplomacy, and looking slightly unimpressed with the lists.
                  The real boss.
                </p>
              </div>
            </div>

            <div
              id="contact"
              className="flex flex-col sm:flex-row items-center gap-6 p-5 sm:p-6 scroll-mt-24"
            >
              <div className="relative shrink-0 w-44 sm:w-40">
                <div
                  aria-hidden
                  className="absolute -inset-[3px] rounded-[1.35rem] bg-[linear-gradient(to_bottom,#CD7F32,#facc15,#22d3ee)] opacity-80"
                />
                <img
                  src="/eliass.png"
                  alt="Eliass"
                  className="relative w-full aspect-[3/4] object-contain object-bottom rounded-[1.2rem] bg-background"
                />
              </div>
              <div className="text-center sm:text-left min-w-0">
                <div className="text-[11px] font-black uppercase tracking-widest text-foreground/40 mb-1">
                  Founder · reports to Mellow
                </div>
                <h2 className="text-2xl font-black tracking-tight mb-2">Eliass</h2>
                <p className="text-sm text-foreground/65 leading-relaxed">
                  Writes the lists, ranks the guesses, and ships a new topic every midnight. Used to
                  do all of that on paper.
                </p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:opacity-80 transition-opacity"
                >
                  <Mail size={15} />
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </section>

        <a
          href="https://ko-fi.com/eliass_al"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-10 flex items-center justify-between gap-3 rounded-2xl px-4 py-3.5 text-black bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)] hover:opacity-90 transition-opacity"
        >
          <div className="text-left min-w-0">
            <div className="text-sm font-bold">Support the game</div>
            <div className="text-xs font-medium text-black/70">
              A coffee helps keep a new topic coming every midnight
            </div>
          </div>
          <Coffee size={20} className="shrink-0" />
        </a>

        <section className="mt-14 pt-10 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40 mb-3">
            Other games
          </div>
          <p className="text-[15px] leading-relaxed text-foreground/75 mb-5">
            If you enjoy this kind of daily puzzle, you can try my new game.
          </p>
          <a
            href="https://www.majoritygame.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 rounded-2xl bg-foreground text-background px-4 py-3.5 hover:opacity-90 transition-opacity"
          >
            <img
              src="/majority-favicon.png"
              alt=""
              className="h-11 w-11 rounded-xl shrink-0 bg-white"
            />
            <span className="min-w-0 text-left flex-1">
              <span className="block text-[10px] font-bold uppercase tracking-widest opacity-60">
                New game
              </span>
              <span className="block text-lg font-black leading-tight">Majority</span>
              <span className="block text-sm opacity-75">Try out our new game.</span>
            </span>
            <ArrowRight
              size={18}
              className="shrink-0 opacity-60 transition-transform group-hover:translate-x-0.5"
            />
          </a>
        </section>
      </motion.div>

      <SiteFooter />
      </ContentLayout>
    </div>
  );
}
